// EnhancedSuggestionEngine.ts - Updated to use current state logic

import type { FilterCriteria } from './vocabulary';
import { humanOperatorToOperator } from './vocabulary';
import { Suggestion, FilterAreaValue } from './types';
import { ParseResult } from './FilterAST';
import { formatCirclePosition } from './formatCirclePosition';

export class EnhancedSuggestionEngine {
  constructor(
    private filterOptions: Record<string, FilterCriteria>,
    private shapeData?: FilterAreaValue[],
  ) {}

  getSuggestions(
    parseResult: ParseResult,
    searchTerm: string = '',
    _input: string = '',
    _cursorPosition: number = 0,
  ): Suggestion[] {
    const { context, ast } = parseResult;

    if (!context) {
      return [];
    }

    // Use partialText from context or fall back to provided searchTerm
    const actualSearchTerm = context.partialText || searchTerm || '';

    // Use currentState to determine suggestions
    switch (context.currentState) {
      case 'TypingField':
      case 'EditingField':
        return this.getFieldSuggestions(actualSearchTerm);

      case 'TypingOperator':
      case 'EditingOperator':
        const fieldName = context.parentExpression?.field?.name;
        return this.getOperatorSuggestions(fieldName, actualSearchTerm);

      case 'TypingValue':
      case 'EditingValue':
        const field = context.parentExpression?.field?.name;
        return this.getValueSuggestions(field, actualSearchTerm);

      case 'TypingLogicalOperator':
      case 'EditingLogicalOperator':
        return this.getLogicalSuggestions(actualSearchTerm);

      case 'BetweenTokens':
        // Use expectsNext when we're between tokens
        switch (context.expectsNext) {
          case 'Field':
            return this.getFieldSuggestions(actualSearchTerm);
          case 'Operator':
            const fieldName2 = context.parentExpression?.field?.name;
            return this.getOperatorSuggestions(fieldName2, actualSearchTerm);
          case 'Value':
            const field2 = context.parentExpression?.field?.name;
            return this.getValueSuggestions(field2, actualSearchTerm);
          case 'LogicalOperator':
            return this.getLogicalSuggestions(actualSearchTerm);
          default:
            return [];
        }

      default:
        return [];
    }
  }

  private getFieldSuggestions(searchTerm: string): Suggestion[] {
    const lowerSearchTerm = searchTerm.toLowerCase();

    // Check if filterOptions is empty/undefined
    if (!this.filterOptions || Object.keys(this.filterOptions).length === 0) {
      return [];
    }

    const availableFields = Object.keys(this.filterOptions);

    const matchingFields = availableFields.filter((field) => {
      const fieldLower = field.toLowerCase();
      return fieldLower.includes(lowerSearchTerm);
    });

    const suggestions = matchingFields
      .sort((a, b) => {
        const aStartsWith = a.toLowerCase().startsWith(lowerSearchTerm);
        const bStartsWith = b.toLowerCase().startsWith(lowerSearchTerm);

        if (aStartsWith && !bStartsWith) return -1;
        if (!aStartsWith && bStartsWith) return 1;

        return a.localeCompare(b);
      })
      .map((field) => {
        return {
          type: 'field' as const,
          value: field.toLowerCase(),
          label: field.toLowerCase(),
          icon: this.filterOptions[field].icon,
          description: `Type: ${this.filterOptions[field].data_types.join(
            ', ',
          )}`,
        };
      });

    return suggestions;
  }

  private getOperatorSuggestions(
    field: string | undefined,
    searchTerm: string,
  ): Suggestion[] {
    if (!field) {
      return [];
    }

    const upperField = field.toUpperCase();
    const fieldConfig = this.filterOptions[upperField];

    if (!fieldConfig) {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const suggestions: Suggestion[] = [];

    fieldConfig.operators.forEach((humanOp) => {
      const symbol = humanOperatorToOperator(humanOp);

      const matchesSymbol = symbol.toLowerCase().includes(lowerSearchTerm);
      const matchesHuman = humanOp.toLowerCase().includes(lowerSearchTerm);

      if (matchesSymbol || matchesHuman) {
        // Handle semantic operators with special syntax
        if (humanOp === 'contains' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "*|*"',
            label: 'contains',
            icon: null,
            description: '= "*...*"',
          });
        } else if (humanOp === 'starts with' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "|*"',
            label: 'starts with',
            icon: null,
            description: '= "...*"',
          });
        } else if (humanOp === 'ends with' && symbol === '=') {
          suggestions.push({
            type: 'operator' as const,
            value: '= "*|"',
            label: 'ends with',
            icon: null,
            description: '= "*..."',
          });
        } else if (humanOp === 'does not contain' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "*|*"',
            label: 'does not contain',
            icon: null,
            description: '!= "*...*"',
          });
        } else if (humanOp === 'does not start with' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "|*"',
            label: 'does not start with',
            icon: null,
            description: '!= "...*"',
          });
        } else if (humanOp === 'does not end with' && symbol === '!=') {
          suggestions.push({
            type: 'operator' as const,
            value: '!= "*|"',
            label: 'does not end with',
            icon: null,
            description: '!= "*..."',
          });
        } else {
          suggestions.push({
            type: 'operator' as const,
            value: symbol,
            label: humanOp,
            icon: null,
            description: symbol,
          });
        }
      }
    });

    return suggestions;
  }

  private getValueSuggestions(
    field: string | undefined,
    searchTerm: string,
  ): Suggestion[] {
    if (!field) return [];

    const upperField = field.toUpperCase();
    const fieldConfig = this.filterOptions[upperField];

    if (!fieldConfig) {
      return [];
    }

    // Special handling for position fields - use shape data if available
    const fieldLower = field.toLowerCase();
    if (fieldLower === 'position') {
      return this.getShapeSuggestions(searchTerm, ['Polygon', 'Circle']);
    }

    if (fieldLower === 'circle_position') {
      // Special handling for circle position - suggest coordinate format instead of shapes
      return this.getCirclePositionSuggestions(searchTerm);
    }

    // For all other fields, proceed with normal value suggestions
    if (fieldConfig.values && fieldConfig.values.length > 0) {
      const lowerSearchTerm = searchTerm.toLowerCase();

      const realSuggestions = fieldConfig.values
        .filter((val) => {
          return String(val).toLowerCase().includes(lowerSearchTerm);
        })
        .sort((a, b) => {
          const aStr = String(a).toLowerCase();
          const bStr = String(b).toLowerCase();
          const aStartsWith = aStr.startsWith(lowerSearchTerm);
          const bStartsWith = bStr.startsWith(lowerSearchTerm);

          if (aStartsWith && !bStartsWith) return -1;
          if (!aStartsWith && bStartsWith) return 1;

          return aStr.localeCompare(bStr);
        })
        .map((val) => ({
          type: 'value' as const,
          value: String(val),
          label: String(val),
          icon: null,
          description: null,
        }));

      // If we have a search term but no matches, return empty array (not hints)
      if (realSuggestions.length === 0 && searchTerm.trim().length > 0) {
        return [];
      }

      // If we have real suggestions, return them
      if (realSuggestions.length > 0) {
        return realSuggestions;
      }

      // No search term and no suggestions - show hints only if no search term
      if (searchTerm.trim().length === 0) {
        return this.getInputHints(upperField, fieldConfig);
      }

      return [];
    }

    return this.getInputHints(upperField, fieldConfig);
  }

  private getShapeSuggestions(
    searchTerm: string,
    allowedGeometryTypes: string[],
  ): Suggestion[] {
    // If no shape data is available, return hints
    if (!this.shapeData || this.shapeData.length === 0) {
      if (searchTerm.trim().length === 0) {
        const geometryType = allowedGeometryTypes[0]?.toLowerCase();
        const label =
          geometryType === 'polygon'
            ? 'Select a polygon...'
            : 'Select a circle...';
        const description =
          geometryType === 'polygon'
            ? 'Choose a polygon area from available shapes'
            : 'Choose a circular area from available shapes';

        return [
          {
            type: 'value' as const,
            value: '',
            label,
            icon: null,
            description,
            isHint: true,
          },
        ];
      }
      return [];
    }

    // Filter shapes by geometry type and search term
    const lowerSearchTerm = searchTerm.toLowerCase();
    const lowerAllowedTypes = allowedGeometryTypes.map((type) =>
      type.toLowerCase(),
    );

    const filteredShapes = this.shapeData.filter((shape) => {
      if (!shape.id || !shape.name) {
        return false;
      }

      if (allowedGeometryTypes.length > 0 && shape.geometryType) {
        if (!lowerAllowedTypes.includes(shape.geometryType.toLowerCase())) {
          return false;
        }
      }

      if (searchTerm.trim().length > 0) {
        return shape.name.toLowerCase().includes(lowerSearchTerm);
      }

      return true;
    });

    return filteredShapes.map((shape) => ({
      type: 'value' as const,
      value: shape.id!,
      label: shape.name!,
      icon: null,
      description: shape.geometryType ? `${shape.geometryType} shape` : 'Shape',
    }));
  }

  private getInputHints(
    field: string,
    fieldConfig: FilterCriteria,
  ): Suggestion[] {
    const hints: Suggestion[] = [];

    // Field-specific hints based on field name patterns
    const fieldLower = field.toLowerCase();

    if (fieldLower.includes('name')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter name...',
        icon: null,
        description: 'Type any name (e.g., "Enterprise", "Titanic")',
        isHint: true,
      });
    } else if (fieldLower.includes('id') || fieldLower.includes('identifier')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter ID...',
        icon: null,
        description: 'Type an identifier (e.g., "12345", "ABC-123")',
        isHint: true,
      });
    } else if (fieldLower.includes('email')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter email...',
        icon: null,
        description: 'Type an email address (e.g., "user@domain.com")',
        isHint: true,
      });
    } else if (fieldLower.includes('phone')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter phone number...',
        icon: null,
        description: 'Type a phone number (e.g., "+1234567890")',
        isHint: true,
      });
    } else if (fieldLower.includes('url') || fieldLower.includes('link')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter URL...',
        icon: null,
        description: 'Type a URL (e.g., "https://example.com")',
        isHint: true,
      });
    } else if (fieldLower.includes('code')) {
      hints.push({
        type: 'value' as const,
        value: '',
        label: 'Enter code...',
        icon: null,
        description: 'Type a code (e.g., "ABC123", "XYZ-789")',
        isHint: true,
      });
    }

    // Data type-specific hints
    fieldConfig.data_types.forEach((dataType) => {
      const typeLower = dataType.toLowerCase();

      if (typeLower.includes('string') || typeLower.includes('text')) {
        if (hints.length === 0) {
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Enter text...',
            icon: null,
            description: 'Type any text value',
            isHint: true,
          });
        }
      } else if (typeLower.includes('number') || typeLower.includes('int')) {
        hints.push({
          type: 'value' as const,
          value: '',
          label: 'Enter number...',
          icon: null,
          description: 'Type a numeric value (e.g., 123, 45.67)',
          isHint: true,
        });
      } else if (typeLower.includes('date')) {
        hints.push({
          type: 'value' as const,
          value: '',
          label: 'Enter date...',
          icon: null,
          description: 'Type a date (e.g., "2024-01-15", "2024/01/15")',
          isHint: true,
        });
      }
    });

    // Add common wildcard patterns if applicable
    if (
      fieldConfig.operators.some((op) =>
        ['contains', 'starts with', 'ends with'].includes(op),
      )
    ) {
      const typeExamples: Record<string, string> = {
        string:
          'Wrap patterns in quotes and use * as a wildcard. Example: "*den*" (contains "den"), "den*" (starts with "den"), "*mark" (ends with "mark").',
        number:
          'Wrap patterns in quotes and use * as a wildcard. Example: "*219*" (contains 219), "219*" (starts with 219), "*219" (ends with 219).',
      };

      fieldConfig.data_types.forEach((dataType) => {
        const typeLower = dataType.toLowerCase();

        if (typeLower.includes('string') || typeLower.includes('text')) {
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Wildcard',
            icon: null,
            description: typeExamples.string,
            isHint: true,
          });
        } else if (typeLower.includes('number') || typeLower.includes('int')) {
          hints.push({
            type: 'value' as const,
            value: '',
            label: 'Wildcard',
            icon: null,
            description: typeExamples.number,
            isHint: true,
          });
        }
      });
    }

    return hints;
  }

  private getLogicalSuggestions(searchTerm: string): Suggestion[] {
    const lowerSearchTerm = searchTerm.toLowerCase();

    return ['AND', 'OR']
      .filter((op) => op.toLowerCase().includes(lowerSearchTerm))
      .map((op) => ({
        type: 'logical' as const,
        value: op,
        label: op.toLowerCase(),
        icon: null,
        description: 'Logical operator',
      }));
  }

  private getCirclePositionSuggestions(searchTerm: string): Suggestion[] {
    // For circle position, suggest existing circle shapes (converted to coordinate format)
    // plus format hints for manual entry

    const suggestions: Suggestion[] = [];

    // Suggest existing circle shapes with (lat, lon, radius) format
    const circleSuggestions = this.getCircleShapeSuggestions(searchTerm);
    suggestions.push(...circleSuggestions);

    // If no search term, show format hints and examples
    if (searchTerm.trim().length === 0) {
      suggestions.push({
        type: 'value' as const,
        value: '(57.03, 9.77, 100000)',
        label: 'Example: Danish area',
        icon: null,
        description: '(latitude, longitude, radius_in_meters)',
        isHint: false,
        noQuotes: true,
      });

      suggestions.push({
        type: 'value' as const,
        value: '',
        label: 'Format: (latitude, longitude, radius)',
        icon: null,
        description:
          'Enter coordinates as (lat, lon, radius_in_meters). Example: (57.03, 9.77, 100000)',
        isHint: true,
        noQuotes: true,
      });

      return suggestions;
    }

    // If user is typing, check if it matches the format and provide hints
    const circleFormatRegex =
      /^\s*\(\s*(-?\d*\.?\d*)\s*,?\s*(-?\d*\.?\d*)?\s*,?\s*(\d*\.?\d*)?\s*\)?\s*$/;
    const match = searchTerm.match(circleFormatRegex);

    if (match) {
      const [, lat, lon, radius] = match;

      // If they're typing in the format, provide helpful completion suggestions
      if (lat && !lon) {
        suggestions.push({
          type: 'value' as const,
          value: `(${lat}, 9.77, 100000)`,
          label: `Complete: (${lat}, longitude, radius)`,
          icon: null,
          description: 'Add longitude and radius in meters',
          isHint: false,
          noQuotes: true,
        });
      } else if (lat && lon && !radius) {
        suggestions.push({
          type: 'value' as const,
          value: `(${lat}, ${lon}, 100000)`,
          label: `Complete: (${lat}, ${lon}, radius)`,
          icon: null,
          description: 'Add radius in meters (e.g., 100000 = 100km)',
          isHint: false,
          noQuotes: true,
        });
      }

      // Always show format reminder when typing coordinates
      suggestions.push({
        type: 'value' as const,
        value: '',
        label: 'Format: (latitude, longitude, radius)',
        icon: null,
        description:
          'Latitude: -90 to 90, Longitude: -180 to 180, Radius: meters',
        isHint: true,
        noQuotes: true,
      });
    } else {
      // If they're typing something that doesn't match the format, guide them
      suggestions.push({
        type: 'value' as const,
        value: '(57.03, 9.77, 100000)',
        label: 'Use coordinate format',
        icon: null,
        description:
          'Circle position must be (latitude, longitude, radius_in_meters)',
        isHint: false,
        noQuotes: true,
      });
    }

    return suggestions;
  }

  /**
   * Suggest existing circle shapes, converting their geometry to (lat, lon, radius) format.
   * GeoJSON stores coordinates as [longitude, latitude], but the filter format is (latitude, longitude, radius).
   */
  private getCircleShapeSuggestions(searchTerm: string): Suggestion[] {
    if (!this.shapeData || this.shapeData.length === 0) {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();

    const circleShapes = this.shapeData.filter((shape) => {
      if (!shape.name || shape.geometryType?.toLowerCase() !== 'circle') {
        return false;
      }
      if (!shape.coordinates || shape.radius == null) {
        return false;
      }
      if (searchTerm.trim().length > 0) {
        return shape.name.toLowerCase().includes(lowerSearchTerm);
      }
      return true;
    });

    return circleShapes.map((shape) => {
      const value = formatCirclePosition(shape.coordinates, shape.radius)!;

      return {
        type: 'value' as const,
        value,
        label: shape.name!,
        icon: null,
        description: `Circle: ${value}`,
        noQuotes: true,
      };
    });
  }
}
